<template>
    <div class="pd-section" v-if="pd">
        <section-header title="Professional Development" icon="fa-graduation-cap"/>

        <ui-card>
            <!-- Hours strip -->
            <div class="stat-strip">
                <div class="stat">
                    <div class="stat-value">{{ pd.hours.earned_total | hours }}</div>
                    <div class="stat-label">{{ unitTitle }} earned with us</div>
                </div>
                <div class="stat">
                    <div class="stat-value">{{ pd.hours.in_progress.potential_hours | hours }}</div>
                    <div class="stat-label">in progress across {{ pd.hours.in_progress.count }} {{ pd.hours.in_progress.count === 1 ? 'course' : 'courses' }}</div>
                </div>
                <div class="stat" v-if="pd.profile.declared">
                    <div class="stat-value">{{ pd.hours.counted + pd.hours.external | hours }}<span class="stat-of"> / {{ pd.profile.target_hours }}</span></div>
                    <div class="stat-label">toward your renewal</div>
                </div>
                <div class="stat" v-else-if="pd.hours.pending_review">
                    <div class="stat-value">{{ pd.hours.pending_review }}</div>
                    <div class="stat-label">{{ pd.hours.pending_review === 1 ? 'application' : 'applications' }} awaiting review</div>
                </div>
            </div>

            <!-- Renewal tracking -->
            <div class="renewal mt-5" v-if="pd.profile.declared">
                <div class="renewal-bar">
                    <div class="seg seg-earned" :style="{width: earnedPct + '%'}"></div>
                    <div class="seg seg-progress" :style="{width: progressPct + '%'}"></div>
                </div>
                <div class="renewal-caption">
                    <strong>{{ pd.hours.counted + pd.hours.external | hours }} of {{ pd.profile.target_hours }}</strong> hours toward your renewal on <strong>{{ pd.profile.renewal_on | dateOnly }}</strong>
                    <span v-if="pd.hours.in_progress.potential_hours"> &middot; {{ pd.hours.in_progress.potential_hours | hours }} more in progress</span>
                    <span v-if="pd.hours.external"> &middot; includes {{ pd.hours.external | hours }} on file elsewhere</span>
                    <span v-if="pd.hours.stem_in_window"> &middot; {{ pd.hours.stem_in_window | hours }} STEM</span>
                    &middot; <a href @click.prevent="openProfile">Edit</a>
                </div>
                <div class="renewal-done mt-2" v-if="pd.hours.remaining_to_target === 0"><i class="fas fa-circle-check has-text-success mr-2"></i>You've reached your renewal target.</div>
            </div>
            <div class="info-box mt-5" v-else>
                <div class="box-main">
                    <div class="box-icon"><i class="fad fa-calendar-check fa-2x has-text-primary"></i></div>
                    <div class="box-body">
                        <div class="box-title">Track Your Renewal</div>
                        <div class="box-text">Tell us when your certificate renews and any hours you already have on file, and we'll track your progress here.</div>
                    </div>
                </div>
                <div class="box-actions">
                    <b-button type="is-primary" size="is-small" outlined @click="openProfile"><i class="fas fa-plus mr-2"></i>Set Up Renewal Tracking</b-button>
                </div>
            </div>


            <!-- Salary lane -->
            <pd-salary-lane class="mt-4" :salary="pd.salary" @profile="openProfile"/>

            <!-- Bundle pitch + calls to action (copy mirrors the builder's
                 entry screen, PackageEntry.vue); the button lands on the
                 builder's entry screen, not a wizard step -->
            <div class="info-box mt-5">
                <div class="box-main">
                    <div class="box-icon"><i class="fad fa-layer-group fa-2x has-text-primary"></i></div>
                    <div class="box-body">
                        <div class="box-title">Build Your Own Bundle</div>
                        <div class="box-text">Buy clock hours once at bundle pricing and spend them across any courses in the catalog &mdash; 33% less than enrolling course by course. Earn now, pay later &mdash; finance it with nothing due today and earn your raise before payments start.</div>
                    </div>
                </div>
                <div class="box-actions">
                    <div class="box-lead"><span class="promo-badge">Save 33% when bundling</span></div>
                    <div class="box-buttons">
                        <b-button type="is-primary" outlined size="is-small" tag="router-link" :to="{name: 'course-catalog'}"><i class="fad fa-browsers mr-2"></i>Browse the Catalog</b-button>
                        <b-button type="is-primary" size="is-small" tag="router-link" :to="{name: 'package-builder'}"><i class="fas fa-layer-group mr-2"></i>Build a Custom Course Bundle</b-button>
                    </div>
                </div>
            </div>

            <!-- Same hand-off the bundle builder offers on every step; the
                 lead form deep-links via ?phase=lead -->
            <div class="info-box mt-4">
                <div class="box-main">
                    <div class="box-icon"><i class="fad fa-life-ring fa-2x has-text-primary"></i></div>
                    <div class="box-body">
                        <div class="box-title">Need a Hand?</div>
                        <div class="box-text">A representative can help you plan your clock hours, choose courses, and handle enrollment and payment setup for you.</div>
                    </div>
                </div>
                <div class="box-actions">
                    <b-button type="is-primary" outlined size="is-small" tag="router-link" :to="{name: 'package-builder', query: {phase: 'lead'}}"><i class="far fa-phone mr-2"></i>Work With a Representative</b-button>
                </div>
            </div>
        </ui-card>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import SectionHeader from "../framework/SectionHeader";
    import UiCard from "../UiCard";
    import PdSalaryLane from "./PdSalaryLane";

    /**
     * Professional-development tracking for Washington clock hours: what
     * the student has earned and is working on, progress toward the renewal
     * they declared, and how far they are from the next salary column —
     * the bundle builder's pitch, kept on the home page.
     */
    export default {
        name: "PdSection",
        components: {SectionHeader, UiCard, PdSalaryLane},
        filters: {
            hours(v) {
                const n = parseFloat(v) || 0;
                return Number.isInteger(n) ? String(n) : n.toFixed(1);
            }
        },
        computed: {
            ...mapGetters(['dashboardPd']),
            pd() {
                return this.dashboardPd;
            },
            unitTitle() {
                return (this.pd.certification && this.pd.certification.unit_title) || 'Clock hours';
            },
            earnedPct() {
                return this.pct(this.pd.hours.counted + this.pd.hours.external);
            },
            // In-progress hours fill only the room left after what's earned
            progressPct() {
                return Math.min(100 - this.earnedPct, this.pct(this.pd.hours.in_progress.potential_hours));
            }
        },
        methods: {
            pct(hours) {
                const target = this.pd.profile.target_hours || 0;
                if (!target) return 0;
                return Math.max(0, Math.min(100, (parseFloat(hours) || 0) / target * 100));
            },
            // Career + renewal settings live on Your Account > Professional
            // Development (the verification modal keeps its own copy for the
            // periodic review, but edits go to the tab)
            openProfile() {
                this.$router.push({name: 'account', params: {section: 'pd'}}).catch(() => {});
            }
        }
    };
</script>

<style lang="scss" scoped>
    .stat-strip {
        display: flex;
        flex-wrap: wrap;
        gap: 2rem;

        .stat-value {
            font-size: 1.8rem;
            font-weight: 800;
            line-height: 1.1;
            color: #336699;

            .stat-of {
                font-size: 1rem;
                font-weight: 600;
                color: #7a7a7a;
            }
        }

        .stat-label {
            font-size: 0.8rem;
            color: #7a7a7a;
        }
    }

    .renewal-bar {
        display: flex;
        height: 0.6rem;
        border-radius: 999px;
        background: #ececec;
        overflow: hidden;

        .seg-earned {
            background: #336699;
        }

        .seg-progress {
            background: #9dbbd8;
        }
    }

    .renewal-caption {
        font-size: 0.85rem;
        color: #555;
        margin-top: 0.5rem;
    }

    .renewal-done {
        font-size: 0.85rem;
    }

    // The bundle pitch and rep hand-off use the global .info-box shell
    // (assets/scss/_cta.scss)

    // Same green pill as the course catalog's bundle promo
    .promo-badge {
        display: inline-block;
        background: #e7f4ec;
        color: #2e7d4f;
        border: 2px solid #fff; // lifts it off the shaded footer
        font-size: 0.7rem;
        font-weight: 900;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 3px 10px;
        border-radius: 999px;
    }

</style>
